import { IComponent } from '@eventr/common'
import { ColoursObj, Delete, Duplicate } from '@eventr/common-storybook'
import { Box, Card, Stack } from '@mui/material'
import cx from 'classnames'
import {
    AnimatePresence,
    motion,
    PanInfo,
    useDragControls,
    useSpring,
} from 'framer-motion'
import { useState } from 'react'
import { useEditSectionContext } from '../../feat/editItinerary/editSectionProvider'
import style from '../../home/HomeScreen.module.css'
import { Expand } from '../../home/components/expand'
import { ComponentFinder } from '../componentFinder'

interface IExpandableCardProps {
    data: any
    renderOptions: IExpandableCardOptions
    parentData?: any
    past?: boolean
}
interface IExpandableCardOptions {
    header: IComponent
    body: IComponent[]
    openAtStart?: boolean
    hideChevron?: boolean
}

export const ExpandableCard = ({
    data,
    renderOptions,
    parentData,
    past,
}: IExpandableCardProps) => {
    const actionDrawersWidth = 125
    const { deleteItem, duplicateItem } = useEditSectionContext()
    const x = useSpring(0, { stiffness: 300, damping: 30 })
    const [deleted, setDeleted] = useState<boolean>(false)
    const controls = useDragControls()
    const [chevroClickOpen, setChevroClickOpen] = useState<boolean>(
        renderOptions.openAtStart || false
    )

    const onDragEnd = (
        _: MouseEvent | TouchEvent | PointerEvent,
        info: PanInfo
    ) => {
        x.stop()

        if (info.offset.x < -actionDrawersWidth + 40) {
            x.set(-actionDrawersWidth)
            return
        }
        if (info.offset.x > -actionDrawersWidth + 110) {
            x.set(0)
            return
        }
        if (info.velocity.x > 0) {
            x.set(0)
        } else {
            x.set(-actionDrawersWidth)
        }
    }

    const onDelete = () => {
        deleteItem?.(data._id)
    }

    return (
        <AnimatePresence onExitComplete={onDelete}>
            {!deleted && (
                <motion.div
                    key={data._id}
                    initial={{ x: 0, opacity: 1 }}
                    exit={{ x: -300, opacity: 0 }}
                >
                    {data && (
                        <Card
                            sx={{
                                marginTop: '8px',
                                width: '100%',
                                position: 'relative',
                                zIndex: 10,
                                backgroundColor:
                                    ColoursObj.backgrounds.secondary,
                            }}
                        >
                            <motion.div
                                className="box"
                                style={{ x }}
                                drag="x"
                                onDragEnd={onDragEnd}
                                dragConstraints={{
                                    left: -actionDrawersWidth,
                                    right: 0,
                                }}
                                dragDirectionLock
                                dragControls={controls}
                                dragElastic={0}
                            >
                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    onClick={() => {
                                        if (renderOptions.hideChevron) return

                                        setChevroClickOpen(!chevroClickOpen)
                                    }}
                                    alignItems={'center'}
                                    padding={'16px'}
                                >
                                    {renderOptions?.header?.componentKey
                                        .length > 0 && (
                                            <Box>
                                                <ComponentFinder
                                                    componentKey={
                                                        renderOptions.header
                                                            .componentKey
                                                    }
                                                    data={data}
                                                    renderOptions={
                                                        renderOptions.header
                                                    }
                                                    parentData={parentData}
                                                    isInPast={past}
                                                />
                                            </Box>
                                        )}
                                    <Stack direction={'row'}>
                                        {renderOptions.body.length > 0 &&
                                            renderOptions.hideChevron !==
                                            true && (
                                                <Stack
                                                    flexDirection={'row'}
                                                    gap="8px"
                                                >
                                                    {/* <CountLabel text={renderOptions.body.length} /> */}
                                                    <Box height={'24px'}>
                                                        <Expand
                                                            expanded={
                                                                chevroClickOpen
                                                            }
                                                        />
                                                    </Box>
                                                </Stack>
                                            )}
                                        <motion.div
                                            style={{
                                                position: 'absolute',
                                                zIndex: 5,
                                                marginTop: '-30px',
                                                marginLeft: '18px',
                                                marginBottom: '-16px',
                                            }}
                                        >
                                            <Stack
                                                width={actionDrawersWidth + 50}
                                                height={'60px'}
                                                direction={'row'}
                                            >
                                                <Box
                                                    height={'100%'}
                                                    width={'65px'}
                                                    display={'flex'}
                                                    alignItems={'center'}
                                                    justifyContent={'center'}
                                                    bgcolor={
                                                        ColoursObj.states
                                                            .warning
                                                    }
                                                >
                                                    <Duplicate
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            duplicateItem?.(
                                                                data._id
                                                            )
                                                        }}
                                                    />
                                                </Box>
                                                <Box
                                                    height={'100%'}
                                                    width={'65px'}
                                                    display={'flex'}
                                                    alignItems={'center'}
                                                    justifyContent={'center'}
                                                    bgcolor={
                                                        ColoursObj.states.error
                                                    }
                                                >
                                                    <Delete
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            setDeleted(true)
                                                        }}
                                                    />
                                                </Box>
                                            </Stack>
                                        </motion.div>
                                    </Stack>
                                </Stack>
                            </motion.div>
                            {renderOptions?.body.length > 0 && (
                                <div
                                    className={cx(
                                        style.animationCardOuter,
                                        chevroClickOpen &&
                                        style.animationCardOuterOpen
                                    )}
                                >
                                    <div
                                        className={style.upNextInner}
                                        style={{
                                            padding: chevroClickOpen
                                                ? '12px'
                                                : '0px 12px',
                                        }}
                                    >
                                        {renderOptions.body.map((c, i) => {
                                            return (
                                                <ComponentFinder
                                                    key={'finder_' + i}
                                                    componentKey={
                                                        c.componentKey
                                                    }
                                                    data={data}
                                                    renderOptions={c}
                                                    parentData={parentData}
                                                    isInPast={past}
                                                />
                                            )
                                        })}
                                    </div>
                                </div>
                            )}
                        </Card>
                    )}
                </motion.div>
            )}
        </AnimatePresence>
    )
}
